import styled, { css, keyframes } from 'styled-components';

import { Text } from '../../../../components/Text/Text';
import { ANIM, ART_STYLE, COLORS, REDUCED_MOTION, SPACE } from '../../../../theme/tokens';

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
`;

export const Head = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[2]};
`;

/* Matches the mood board's caption (icon + label), so the ingredient headings read as one set. */
export const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  color: ${COLORS.textMuted};
`;

export const Label = styled(Text)`
  font-size: 14px;
  color: ${COLORS.text};
`;

export const Count = styled(Text)`
  margin-left: auto;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: ${COLORS.textFaint};
`;

export const Hint = styled(Text)`
  font-size: 11.5px;
  line-height: 1.4;
  color: ${COLORS.textFaint};
`;

export const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
`;

/** "+ Add mascot": a dashed, full-width row in the add tiles' material. */
export const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${SPACE[8]};
  width: 100%;
  padding: ${SPACE[8]} ${SPACE[12]};
  border-radius: 12px;
  border: 1px dashed ${COLORS.hairline};
  background: transparent;
  color: ${COLORS.textMuted};
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition:
    border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover,
  &:focus-visible {
    border-color: ${COLORS.ctaSolid};
    color: ${COLORS.textBright};
  }
`;

/* ── one mascot ──────────────────────────────────────────────────────────── */

export const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
  padding: ${SPACE[8]};
  border-radius: 14px;
  border: 1px solid ${COLORS.hairline};
  background: transparent;
`;

export const CardHead = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  min-width: 0;
`;

/** The name, editable in place: an invisible field until hovered or focused (like the style's name). While
 *  Lumio's suggestion types in, it reads in the accent and settles back. */
export const NameInput = styled.input<{ $revealing: boolean }>`
  flex: 1 1 auto;
  min-width: 0;
  box-sizing: border-box;
  padding: ${SPACE[4]} ${SPACE[8]};
  border-radius: 8px;
  border: 1px solid transparent;
  background: transparent;
  color: ${({ $revealing }) => ($revealing ? COLORS.accent : COLORS.text)};
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  outline: none;
  transition:
    color ${ART_STYLE.CHIP_ENTER_MS}ms ${ANIM.EASE},
    border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &::placeholder {
    color: ${COLORS.textFaint};
    font-weight: 500;
  }

  &:hover {
    border-color: ${COLORS.hairline};
  }

  &:focus {
    border-color: ${COLORS.ctaSolid};
    background: ${COLORS.surfaceInset};
  }
`;

const pulse = keyframes`
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.35;
  }
`;

/** A small round icon action on the card (Suggest a name, Remove). */
export const IconAction = styled.button<{ $working?: boolean }>`
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 999px;
  border: 1px solid transparent;
  background: transparent;
  color: ${COLORS.icon};
  cursor: pointer;
  transition:
    color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover:not(:disabled),
  &:focus-visible {
    color: ${COLORS.textBright};
    background: ${COLORS.surfaceInset};
  }

  &:disabled {
    cursor: default;
    opacity: ${({ $working }) => ($working ? 1 : 0.4)};
  }

  ${({ $working }) =>
    $working
      ? css`
          color: ${COLORS.accent};

          svg {
            animation: ${pulse} ${ANIM.LOGO_PLACEHOLDER_PULSE_MS}ms ease-in-out infinite;
          }

          ${REDUCED_MOTION} {
            svg {
              animation: none;
            }
          }
        `
      : ''}
`;
